/**
 * Shift performance data layer – pure async functions for computing, upserting,
 * and reading shift_performance rows.  No React, no useQuery.
 */

import { supabase } from "@/integrations/supabase/client";
import { getShiftPeriod, type ShiftPeriod } from "@/lib/shiftUtils";

export interface ShiftPerformanceRecord {
  company_id: string;
  date: string;        // YYYY-MM-DD
  shift_period: ShiftPeriod;
  platform: string;
  ticket_count: number;
  net_sales: number;
  gross_sales: number;
  sales_tax: number;
  tips: number;
  source?: string;
}

/**
 * Minimal transaction shape used by the computation function.
 * Matches the columns we SELECT from sales_transactions.
 */
export interface TransactionForShift {
  order_date?: string | null;
  order_time?: string | null;
  sale_date?: string | null;
  sale_time?: string | null;
  platform?: string | null;
  gross_sales?: number | null;
  net_payout_amount?: number | null;
  net_deposit_amount?: number | null;
  total_payout_amount?: number | null;
  tip_amount?: number | null;
  taxable_subtotal?: number | null;
  source?: string | null;
}

/**
 * Compute shift-level metrics from a set of sales_transactions rows.
 * Prefers `order_time` (populated by auto sync) and falls back to `sale_time`.
 */
export function computeShiftMetricsFromTransactions(
  companyId: string,
  transactions: TransactionForShift[],
): ShiftPerformanceRecord[] {
  const map = new Map<string, ShiftPerformanceRecord>();

  for (const tx of transactions) {
    const timeStr = tx.order_time ?? tx.sale_time ?? null;
    const shift = getShiftPeriod(timeStr);
    if (!shift) continue;

    const dateStr = tx.order_date ?? tx.sale_date;
    if (!dateStr) continue;

    const platform = tx.platform ?? 'unknown';
    const key = `${dateStr}|${shift}|${platform}`;

    if (!map.has(key)) {
      map.set(key, {
        company_id: companyId,
        date: dateStr,
        shift_period: shift,
        platform,
        ticket_count: 0,
        net_sales: 0,
        gross_sales: 0,
        sales_tax: 0,
        tips: 0,
        source: tx.source ?? undefined,
      });
    }

    const rec = map.get(key)!;
    rec.ticket_count += 1;
    rec.gross_sales += Number(tx.gross_sales) || 0;
    rec.net_sales +=
      Number(tx.net_payout_amount) ||
      Number(tx.net_deposit_amount) ||
      Number(tx.total_payout_amount) ||
      Number(tx.gross_sales) ||
      0;
    rec.tips += Number(tx.tip_amount) || 0;
    rec.sales_tax += Number(tx.taxable_subtotal) || 0;
  }

  return Array.from(map.values());
}

/**
 * Upsert pre-computed shift records into the shift_performance table.
 * Uses the unique constraint (company_id, date, shift_period, platform).
 */
export async function upsertShiftPerformance(
  records: ShiftPerformanceRecord[],
): Promise<void> {
  if (records.length === 0) return;

  const rows = records.map((r) => ({
    company_id: r.company_id,
    date: r.date,
    shift_period: r.shift_period,
    platform: r.platform,
    ticket_count: r.ticket_count,
    net_sales: r.net_sales,
    gross_sales: r.gross_sales,
    sales_tax: r.sales_tax,
    tips: r.tips,
    source: r.source ?? null,
  }));

  const { error } = await supabase
    .from("shift_performance")
    .upsert(rows as any, {
      onConflict: "company_id,date,shift_period,platform",
      ignoreDuplicates: false,
    });

  if (error) {
    console.error("[upsertShiftPerformance] error:", error.message);
  }
}

/**
 * Convenience: compute metrics from transactions and upsert in one call.
 */
export async function computeAndUpsertShiftPerformance(
  companyId: string,
  transactions: TransactionForShift[],
): Promise<void> {
  const records = computeShiftMetricsFromTransactions(companyId, transactions);
  await upsertShiftPerformance(records);
}

/**
 * Read shift_performance rows for a company within a date range.
 */
export async function getShiftPerformance(
  companyId: string,
  startDate: string,
  endDate: string,
): Promise<ShiftPerformanceRecord[]> {
  const { data, error } = await supabase
    .from("shift_performance")
    .select("*")
    .eq("company_id", companyId)
    .gte("date", startDate)
    .lte("date", endDate)
    .order("date", { ascending: true });

  if (error) {
    console.error("[getShiftPerformance] error:", error.message);
    return [];
  }

  return (data ?? []) as unknown as ShiftPerformanceRecord[];
}
